import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import { describeError } from '@cf-shared/error-describe';
import { LoggingService } from 'services/logging.service';
import { UrlQueryService } from 'services/url-query-service';
import {
  clearPromoCode,
  PromoCodeProblemReporter,
  PromoCodeStorage,
  PromoCodeStorageProblem,
  readPromoCode,
  rememberPromoCode,
} from './promo-code';

/** The URL parameter a sale link carries the code in: `/promo?code=BLACKFRIDAY`. */
export const PROMO_CODE_URL_PARAM = 'code';

/**
 * Keeps a promo-campaign code (#6965) from the moment it arrives — a sale link, or
 * the "Have a code?" field — until the buyer completes a purchase or the server says
 * the campaign has ended. The rules live in `promo-code.ts`; this supplies the
 * browser's `localStorage`, the clock and the reporting.
 *
 * Every storage failure is info tier: it is an expected browser limit (Safari's
 * private mode refuses writes), and the only cost is that the buyer types the code.
 * See design-docs/promo-campaigns.md.
 */
@Injectable({ providedIn: 'root' })
export class PromoCodeService {
  constructor(
    private urlQuery: UrlQueryService,
    private logging: LoggingService,
  ) {}

  private readonly reportProblem: PromoCodeProblemReporter = (problem: PromoCodeStorageProblem, err: unknown): void => {
    this.logging.trackSilentInfo(problem, { error: describeError(err) });
  };

  /**
   * The browser's local storage, or undefined when even reaching it throws (some
   * browsers do this with site data blocked). Then the code is simply not kept.
   */
  private storage(): PromoCodeStorage | undefined {
    try {
      return window.localStorage;
    } catch (err) {
      this.reportProblem(PromoCodeStorageProblem.Unreadable, err);
      return undefined;
    }
  }

  private nowUtc(): string {
    return DateTime.utc().toISO();
  }

  /**
   * Keep the code the app was opened with (`?code=`), if there is one, and drop it
   * from the address so a later visit to the page does not put it back after a
   * purchase has cleared it.
   */
  captureFromUrl(): void {
    const code = this.urlQuery.getUrlParam(PROMO_CODE_URL_PARAM);
    if (!code) return;
    this.remember(code);
    this.urlQuery.clearUrlSearchKeys([PROMO_CODE_URL_PARAM]);
  }

  /** Keep a code the buyer typed or a link carried. */
  remember(code: string): void {
    const storage = this.storage();
    if (storage) rememberPromoCode(storage, code, this.nowUtc(), this.reportProblem);
  }

  /** The kept code, or undefined. */
  getCode(): string | undefined {
    const storage = this.storage();
    return storage ? readPromoCode(storage, this.nowUtc(), this.reportProblem) : undefined;
  }

  /** Forget the kept code: after a purchase, or when the server says the campaign has ended. */
  clear(): void {
    const storage = this.storage();
    if (storage) clearPromoCode(storage);
  }
}
